/**
 * @fileoverview Google OAuth signup button for students
 * @description Button to initiate Google OAuth signup flow - completes signup automatically
 */

import { useState, useEffect, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Alert, Stack } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconAlertCircle, IconX } from '@tabler/icons-react'
import { toast } from 'react-toastify'
import { EmailAlreadyRegisteredError } from 'helpers'
import firebase from 'firebase/app'
import GoogleIcon from '@legacy/common/Icons/Google'
import { initFirebaseApp } from '@legacy/common/Tools/Firebase'
import { useStudentSignupContext } from '../context/StudentSignupContext'
import { googleSignupStudent } from '../service/studentSignup'

interface GoogleSignupButtonProps {
  /** Whether button is disabled */
  readonly disabled?: boolean
}

/**
 * Google OAuth signup button for students
 * Handles Firebase popup authentication and completes signup automatically
 */
export const GoogleSignupButton = memo(({ disabled }: GoogleSignupButtonProps) => {
  const { t } = useTranslation()
  const { data, setUser } = useStudentSignupContext()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    initFirebaseApp()
  }, [])

  /**
   * Handle Google signup flow:
   * 1. Open Firebase Google popup
   * 2. Get user credentials from Google
   * 3. Call API to complete signup
   * 4. Show success screen
   */
  const handleGoogleSignup = async () => {
    setLoading(true)
    setError(null)

    try {
      // Step 1: Firebase Google OAuth popup
      const provider = new firebase.auth.GoogleAuthProvider()
      const result = await firebase.auth().signInWithPopup(provider)

      if (!result.user) {
        throw new Error(t('student_signup.google_no_user_data'))
      }

      const { user } = result

      // Step 2: Extract user data
      const email = user.email || ''
      const firstName = user.displayName?.split(' ')[0] || ''
      const lastName = user.displayName?.split(' ').slice(1).join(' ') || ''

      // Step 3: Call API to complete signup
      const response = await googleSignupStudent(
        {
          email,
          uid: user.uid,
          displayName: user.displayName || undefined,
          firstName,
          lastName,
        },
        undefined,
        data.inviteCode
      )

      // Step 4: Set user to show success screen
      setUser(response.user)
      toast.success(t('student_signup.signup_success'))

    } catch (error: unknown) {
      // Check if it's a Firebase error (has .code property)
      const firebaseError = error as { code?: string }

      if (firebaseError.code) {
        // Silent errors - user closed popup
        if (firebaseError.code === 'auth/popup-closed-by-user') return
        if (firebaseError.code === 'auth/cancelled-popup-request') return

        // Firebase popup blocked
        if (firebaseError.code === 'auth/popup-blocked') {
          toast.error(t('student_signup.popup_blocked'))
          return
        }

        // Firebase network error
        if (firebaseError.code === 'auth/network-request-failed') {
          toast.error(t('student_signup.network_error'))
          return
        }
      }

      // Handle email already registered
      if (error instanceof EmailAlreadyRegisteredError) {
        showNotification({
          title: 'Email Already Registered',
          message: 'The email address you entered is already associated with an existing account. Please log in or use a different email to sign up.',
          color: 'red',
          icon: <IconX size={18} />,
          autoClose: 8000,
        })
        return
      }

      // API or other errors - show message to user
      const errorMessage = error instanceof Error
        ? error.message
        : t('student_signup.google_signup_error')

      console.error('Google signup error:', error)
      setError(errorMessage)
      toast.error(errorMessage)

    } finally {
      setLoading(false)
    }
  }

  return (
    <Stack spacing='sm'>
      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color='red'
          withCloseButton
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      <Button
        fullWidth
        size='lg'
        variant='default'
        leftIcon={<GoogleIcon />}
        disabled={disabled || loading}
        loading={loading}
        onClick={handleGoogleSignup}
        aria-label={t('student_signup.sign_up_with_google')}
      >
        {t('student_signup.sign_up_with_google')}
      </Button>
    </Stack>
  )
})

GoogleSignupButton.displayName = 'GoogleSignupButton'
